import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Button, 
    Box, 
    Card, 
    Typography, 
    Stack,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Paper,
    TextField,
    IconButton,
    Toolbar,
    InputAdornment} from '@mui/material';
import { 
    Add, 
    Edit, 
    Delete,
    Search,
    FilterList,
    Description
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { getImageUrl } from '../../../../services/api';
import vacanyApi from '../../../../services/vacanyApi';

const VacancyIndex = () => {
    const [vacancies, setVacancies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('id');
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchVacancies = async () => {
            try {
                const response = await vacanyApi.getAll();
                console.log('Fetched vacancies:', response);
                setVacancies(response);
            } catch (error) {
                console.error('Fetch error:', error);
            } finally {
                setLoading(false);
            }
        };
    
        fetchVacancies();
    }, []);

    const handleDelete = async (id) => {
        const vacancyToDelete = vacancies.find(vacancy => vacancy.id === id);
        
        const confirm = await Swal.fire({
            title: 'Confirm Delete',
            text: `Are you sure you want to delete the vacancy "${vacancyToDelete.title}"? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });
    
        if (!confirm.isConfirmed) return;
    
        try {
            await vacanyApi.delete(id);
            setVacancies(vacancies.filter(vacancy => vacancy.id !== id));
            
            Swal.fire(
                'Deleted!',
                'Vacancy has been deleted successfully.',
                'success'
            );
        } catch (error) {
            console.error('Error deleting vacancy:', error);
            Swal.fire(
                'Error!',
                error.response?.data?.error || 'Failed to delete vacancy',
                'error'
            );
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const descendingComparator = (a, b, orderBy) => {
        if (b[orderBy] < a[orderBy]) return -1;
        if (b[orderBy] > a[orderBy]) return 1;
        return 0;
    };

    const getComparator = (order, orderBy) => {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    };

    const stableSort = (array, comparator) => {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    };

    const filteredVacancies = vacancies.filter(vacancy => 
        vacancy.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (vacancy.description && vacancy.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (vacancy.location && vacancy.location.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const sortedVacancies = stableSort(filteredVacancies, getComparator(order, orderBy));
    const paginatedVacancies = sortedVacancies.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    const FilePreview = ({ fileUrl, fileName }) => {
        const handleDownload = () => {
            if (!fileUrl) return;
            
            const downloadUrl = getImageUrl(fileUrl);
            const downloadFileName = fileName || fileUrl.split('/').pop();
            
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = downloadFileName;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        };

        if (!fileUrl) {
            return (
                <Typography variant="body2" color="textSecondary">
                    No file
                </Typography>
            );
        }

        const displayName = fileName || fileUrl.split('/').pop();

        return (
            <Box 
                onClick={handleDownload}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    '&:hover': {
                        textDecoration: 'underline',
                        color: 'primary.main'
                    }
                }}
            >
                <Description fontSize="small" color="primary" />
                <Typography variant="body2">
                    {displayName || 'Download'}
                </Typography>
            </Box>
        );
    };

    const StatusChip = ({ deadline }) => {
        if (!deadline) {
            return <Chip label="Unknown" color="default" size="small" />;
        }
        
        const today = new Date();
        const closing = new Date(deadline);
        const isOpen = closing > today;
        
        return (
            <Chip 
                label={isOpen ? 'Open' : 'Closed'} 
                color={isOpen ? 'success' : 'error'}
                size="small"
            />
        );
    };

    const TypeChip = ({ type }) => {
        const typeColors = {
            'Full-time': 'primary',
            'Part-time': 'secondary',
            'Contract': 'info',
            'Internship': 'success',
            'Volunteer': 'error'
        };

        return (
            <Chip 
                label={type} 
                color={typeColors[type] || 'default'}
                size="small"
            />
        );
    };

    const CategoryChip = ({ category }) => {
        const categoryColors = {
            'Job': 'primary',
            'Scholarship': 'secondary',
            'Internship': 'success',
            'Volunteer': 'error'
        };

        return (
            <Chip 
                label={category} 
                color={categoryColors[category] || 'default'}
                size="small"
            />
        );
    };

    const columns = [
        { 
            id: 'serialNo', 
            label: '#', 
            width: '80px', 
            sortable: false,
            format: (value, row, page, rowsPerPage, index) => (
                <Typography variant="body2">
                    {(page * rowsPerPage) + index + 1}
                </Typography>
            )
        },
        { 
            id: 'title', 
            label: 'Title', 
            width: '250px', 
            sortable: true,
            format: (value) => (
                <Typography variant="subtitle2" noWrap>
                    {value}
                </Typography>
            )
        },
        { 
            id: 'type', 
            label: 'Type', 
            width: '120px',
            sortable: true,
            format: (value) => <TypeChip type={value} />
        },
        { 
            id: 'category', 
            label: 'Category', 
            width: '120px',
            sortable: true,
            format: (value) => <CategoryChip category={value} />
        },
        { 
            id: 'location', 
            label: 'Location', 
            width: '150px',
            sortable: true,
            format: (value) => (
                <Typography variant="body2" noWrap>
                    {value || '-'}
                </Typography>
            )
        },
        { 
            id: 'postedDate', 
            label: 'Posted Date', 
            width: '150px',
            sortable: true,
            format: (value) => formatDate(value)
        },
        { 
            id: 'deadline', 
            label: 'Deadline', 
            width: '150px',
            sortable: true,
            format: (value) => formatDate(value)
        },
        { 
            id: 'status', 
            label: 'Status', 
            width: '120px',
            align: 'center',
            sortable: true,
            format: (value, row) => <StatusChip deadline={row.deadline} />
        },
        { 
            id: 'fileUrl',
            label: 'Document', 
            width: '180px',
            sortable: false,
            format: (value, row) => (
                <FilePreview 
                    fileUrl={value} 
                    fileName={`Vacancy_${row.id}.pdf`}
                />
            )
        },
        { 
            id: 'actions', 
            label: 'Actions', 
            width: '120px',
            align: 'center',
            sortable: false,
            format: (value, row) => (
                <>
                    <IconButton
                        aria-label="edit"
                        onClick={() => navigate(`/admin/vacancies/${row.id}`, { state: { vacancyData: row } })}
                        size="small"
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                        aria-label="delete"
                        onClick={() => handleDelete(row.id)}
                        size="small"
                    >
                        <Delete fontSize="small" />
                    </IconButton>
                </>
            )
        }
    ];

    return (
        <Box sx={{ width: '100%' }}>
            <Stack 
                direction="row" 
                alignItems="center" 
                justifyContent="space-between" 
                mb={3}
                sx={{
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 2, sm: 0 }
                }}
            >
                <Typography 
                    variant="h4" 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '1.7rem' },
                        fontWeight: 600
                    }}
                >
                    Vacancy Management
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => navigate('/admin/vacancies/new')}
                    sx={{ 
                        boxShadow: 'none',
                        textTransform: 'capitalize',
                        borderRadius: '6px',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '6px 12px', sm: '8px 16px' }
                    }}
                >
                    New Vacancy
                </Button>
            </Stack>

            <Card sx={{ 
                p: 2,
                boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
                borderRadius: '12px'
            }}>
                <Paper sx={{ width: '100%', mb: 2 }}>
                    <Toolbar
                        sx={{
                            pl: { sm: 2 },
                            pr: { xs: 1, sm: 1 },
                        }}
                    >
                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Search vacancies..."
                            value={searchTerm}
                            onChange={handleSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: '300px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px'
                                }
                            }}
                        />
                        <Box sx={{ flex: '1 1 100%' }} />
                        <IconButton>
                            <FilterList />
                        </IconButton>
                    </Toolbar>
                    
                    <TableContainer>
                        <Table
                            sx={{ minWidth: 750 }}
                            aria-labelledby="tableTitle"
                            size={'medium'}
                        >
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.align || 'left'}
                                            width={column.width}
                                            sortDirection={orderBy === column.id ? order : false}
                                            sx={{
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                backgroundColor: 'background.paper',
                                                borderBottom: '1px solid',
                                                borderColor: 'divider'
                                            }}
                                        >
                                            {column.sortable ? (
                                                <TableSortLabel
                                                    active={orderBy === column.id}
                                                    direction={orderBy === column.id ? order : 'asc'}
                                                    onClick={() => handleRequestSort(column.id)}
                                                >
                                                    {column.label}
                                                </TableSortLabel>
                                            ) : (
                                                column.label
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center">
                                            Loading vacancies...
                                        </TableCell>
                                    </TableRow>
                                ) : paginatedVacancies.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center">
                                            {searchTerm ? 'No matching vacancies found' : 'No vacancies available'}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedVacancies.map((row, index) => (
                                        <TableRow
                                            hover
                                            key={row.id}
                                            sx={{
                                                '& .MuiTableCell-root': {
                                                    borderBottom: '1px dashed',
                                                    borderColor: 'divider'
                                                }
                                            }}
                                        >
                                            {columns.map((column) => {
                                                const value = row[column.id];
                                                return (
                                                    <TableCell 
                                                        key={column.id}
                                                        align={column.align}
                                                    >
                                                        {column.format 
                                                            ? column.format(value, row, page, rowsPerPage, index)
                                                            : (
                                                                <Typography 
                                                                    variant="body2"
                                                                    noWrap
                                                                >
                                                                    {value || '-'}
                                                                </Typography>
                                                            )
                                                        }
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 20, 50]}
                        component="div"
                        count={filteredVacancies.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            </Card>
        </Box>
    );
};

export default VacancyIndex;